import type { Rect, UiRects } from "../../game/input";
import { getSettingsPageCount, getSettingsPageDefinition } from "../../game/settingsPages";
import type { RenderModel } from "../renderModel";
import { GAME_FONT, roundedRectPath, type PhoneMetrics } from "../renderConstants";
import { drawAboutContent } from "./drawAbout";

// ── Dependency interface ────────────────────────────────────────────

export interface PhoneSettingsDeps {
  drawPhoneEditUsername(
    ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number,
    metrics: PhoneMetrics,
  ): void;
  staggerAlpha(index: number, delayMs: number, durationMs: number): number;
  drawNotificationPanel(
    rect: Rect,
    options: { fill: string; stroke?: string; accent?: string; radius?: number; lineWidth?: number },
  ): void;
  drawNotificationChip(
    rect: Rect, label: string, accent: string, fontPx: number,
  ): void;
  measureTextWidth(text: string): number;
  aboutHeadLogoImage: HTMLImageElement | null;
  aboutWordmarkLogoImage: HTMLImageElement | null;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawPhoneSettingsScreen(
  deps: PhoneSettingsDeps,
  ctx: CanvasRenderingContext2D,
  model: RenderModel,
  uiRects: UiRects,
  scrX: number, scrY: number, scrW: number, scrH: number,
  contentX: number, contentW: number, contentRight: number,
  metrics: PhoneMetrics,
): void {
  const desktop = metrics.desktop;
  let curY = scrY + metrics.topPad;

  // ── Edit username sub-view ──
  if (model.phoneEditUsernameMode) {
    deps.drawPhoneEditUsername(ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, metrics);
    return;
  }

  ctx.fillStyle = "#ff6b9d";
  ctx.font = `bold ${metrics.titleFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";

  const p = model.performance;
  const page = model.settingsPage;
  type RectBackedUiRectKey = {
    [K in keyof UiRects]-?: UiRects[K] extends Rect | undefined ? K : never
  }[keyof UiRects];
  type SettingToggleRow = {
    kind: "toggle";
    label: string;
    value: string;
    color: string;
    rectKey: RectBackedUiRectKey;
  };
  type SettingSliderRow = {
    kind: "slider";
    label: string;
    volume: number;
    accent: string;
    trackStroke: string;
    fillColor: string;
    valueColor: string;
    rectKey: "musicVolume" | "sfxVolume";
  };
  type SettingRow = SettingToggleRow | SettingSliderRow;
  const soundRows: SettingRow[] = [
      {
        kind: "toggle",
        label: "MUSIC",
        value: model.musicEnabled ? "ON" : "OFF",
        color: model.musicEnabled ? "#4dff88" : "#ff6b6b",
        rectKey: "settingsMusic"
      },
      {
        kind: "toggle",
        label: "SFX",
        value: model.sfxEnabled ? "ON" : "OFF",
        color: model.sfxEnabled ? "#4dff88" : "#ff6b6b",
        rectKey: "settingsSfx"
      },
      {
        kind: "slider",
        label: "MUSIC VOLUME",
        volume: model.musicVolume,
        accent: "#64d8ff",
        trackStroke: "rgba(124,168,234,0.85)",
        fillColor: "rgba(0,242,255,0.75)",
        valueColor: "#7a9cc7",
        rectKey: "musicVolume"
      },
      {
        kind: "slider",
        label: "SFX VOLUME",
        volume: model.sfxVolume,
        accent: "#ffbf6b",
        trackStroke: "rgba(234,168,124,0.85)",
        fillColor: "rgba(255,180,0,0.78)",
        valueColor: "#d7a47e",
        rectKey: "sfxVolume"
      },
      {
        kind: "toggle",
        label: "HAPTICS",
        value: model.hapticsEnabled ? "ON" : "OFF",
        color: model.hapticsEnabled ? "#4dff88" : "#ff6b6b",
        rectKey: "settingsHaptics"
      },
      { kind: "toggle", label: "PRESET", value: p.preset.toUpperCase(), color: "#64d8ff", rectKey: "settingsPreset" },
      { kind: "toggle", label: "SIDE FX", value: p.sideFxMode.toUpperCase(), color: "#9ec2ff", rectKey: "settingsSideFx" },
      { kind: "toggle", label: "SFX DENSITY", value: p.sideFxDensityMode.toUpperCase(), color: "#9ec2ff", rectKey: "settingsSideFxDensity" },
  ];
  const backgroundRows: SettingRow[] = [
      { kind: "toggle", label: "BG STYLE", value: p.backgroundStyleMode.toUpperCase(), color: "#a78bfa", rectKey: "settingsBgStyle" },
      { kind: "toggle", label: "BLOOM", value: p.backgroundBloomMode.toUpperCase(), color: "#a78bfa", rectKey: "settingsBgBloom" },
      { kind: "toggle", label: "GLOW", value: p.backgroundGlowMode.toUpperCase(), color: "#a78bfa", rectKey: "settingsBgGlow" },
      { kind: "toggle", label: "SATURATION", value: p.backgroundSaturationMode.toUpperCase(), color: "#a78bfa", rectKey: "settingsBgSaturation" },
      { kind: "toggle", label: "MOTION", value: p.backgroundMotionMode.toUpperCase(), color: "#a78bfa", rectKey: "settingsBgMotion" },
  ];
  const effectsRows: SettingRow[] = [
      { kind: "toggle", label: "VISUAL MIX", value: p.backgroundMixMode.toUpperCase(), color: "#64d8ff", rectKey: "settingsBgMix" },
      { kind: "toggle", label: "PARTICLES", value: p.particleMode.toUpperCase(), color: "#9ec2ff", rectKey: "settingsParticles" },
      { kind: "toggle", label: "OPPONENT", value: p.opponentRenderMode.toUpperCase(), color: "#9ec2ff", rectKey: "settingsOpponent" },
  ];
  const accountRows: SettingRow[] = [
      { kind: "toggle", label: "USERNAME", value: (model.username ?? "NOT SET").toUpperCase(), color: "#ffd75a", rectKey: "changeUsername" },
      { kind: "toggle", label: "APPEAR OFFLINE", value: model.appearOffline ? "ON" : "OFF", color: model.appearOffline ? "#ff9966" : "#4dff88", rectKey: "toggleAppearOffline" },
      ...(model.pushSupported
        ? [{
            kind: "toggle" as const,
            label: "PUSH NOTIFS",
            value: model.pushEnabled ? "ON" : "OFF",
            color: model.pushEnabled ? "#4dff88" : "#ff9966",
            rectKey: "togglePushNotifications" as const
          }]
        : []),
      { kind: "toggle", label: "LOGOUT", value: "", color: "#ff6b6b", rectKey: "settingsLogout" },
  ];
  const totalPages = getSettingsPageCount({ surface: "phone", canUseDebugSettings: model.canUseDebugSettings });
  const pageDef = getSettingsPageDefinition({ surface: "phone", canUseDebugSettings: model.canUseDebugSettings }, page);
  const clampedPage = Math.min(page, totalPages - 1);
  const pageTitle = pageDef.phoneTitle;

  ctx.fillText(pageTitle, scrX + scrW / 2, curY);
  curY += metrics.titleGap;

  const rowH = metrics.rowH;
  const rowGap = metrics.rowGap;
  const sliderTrackInset = 10;
  const sliderTrackH = desktop ? 6 : 8;
  const sliderHitPad = desktop ? 3 : 4;
  const navBtnH = metrics.ctaH;
  const btnGap = 4;
  const bottomY = scrY + scrH - navBtnH - 6;
  const contentBottomLimit = bottomY - 8;

  if (pageDef.key === "about") {
    drawAboutContent(
      {
        ctx,
        aboutHeadLogoImage: deps.aboutHeadLogoImage,
        aboutWordmarkLogoImage: deps.aboutWordmarkLogoImage,
      },
      {
        centerX: scrX + scrW / 2,
        topY: curY + (desktop ? 10 : 8),
        maxWidth: contentW - 20,
        bodyFontPx: desktop ? 7 : 6,
        bodyLineHeight: desktop ? 15 : 13,
        logoGapY: desktop ? 18 : 14,
        logoInlineGap: desktop ? 10 : 8,
        headMaxHeight: desktop ? 42 : 30,
        wordmarkMaxHeight: desktop ? 29 : 20,
        fallbackFontPx: desktop ? 7 : 6,
      },
    );
  } else {
    const rows = pageDef.key === "sound"
      ? soundRows
      : pageDef.key === "background"
        ? backgroundRows
        : pageDef.key === "effects"
          ? effectsRows
          : accountRows;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (curY + rowH > contentBottomLimit) break;

      const alpha = deps.staggerAlpha(i, 40, 180);
      if (alpha <= 0) { curY += rowH + rowGap; continue; }
      if (alpha < 1) { ctx.save(); ctx.globalAlpha = alpha; }

      const rect: Rect = { x: contentX, y: curY, w: contentW, h: rowH };

      // Row bg
      if (desktop) {
        deps.drawNotificationPanel(rect, {
          fill: "rgba(16,29,49,0.95)",
          stroke: "rgba(92,140,212,0.2)",
          accent: row.kind === "slider" ? row.accent : row.color,
          radius: metrics.cardRadius
        });
      } else {
        ctx.fillStyle = "#162030";
        ctx.fillRect(contentX, curY, contentW, rowH);
        ctx.fillStyle = row.kind === "slider" ? row.accent : row.color;
        ctx.fillRect(contentX, curY, 3, rowH);
      }

      if (row.kind === "slider") {
        const clampedVolume = Math.max(0, Math.min(1, row.volume));
        const valueText = `${Math.round(clampedVolume * 100)}%`;
        const labelY = curY + (desktop ? 8 : 9);
        const trackX = rect.x + sliderTrackInset;
        const trackW = rect.w - sliderTrackInset * 2;
        const trackY = rect.y + rect.h - sliderTrackH - (desktop ? 5 : 4);
        const progressW = Math.max(0, trackW * clampedVolume);
        const knobX = trackX + progressW;
        const knobY = trackY + sliderTrackH / 2;
        const trackRadius = sliderTrackH / 2;

        uiRects[row.rectKey] = {
          x: trackX,
          y: trackY - sliderHitPad,
          w: trackW,
          h: sliderTrackH + sliderHitPad * 2
        };

        ctx.fillStyle = "#c8d8f0";
        ctx.font = `bold ${metrics.helperFontPx}px ${GAME_FONT}`;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(row.label, contentX + 10, labelY);

        ctx.fillStyle = row.valueColor;
        ctx.textAlign = "right";
        ctx.fillText(valueText, contentX + contentW - 8, labelY);

        roundedRectPath(ctx, trackX, trackY, trackW, sliderTrackH, trackRadius);
        ctx.fillStyle = "rgba(25,38,62,0.95)";
        ctx.fill();
        ctx.strokeStyle = row.trackStroke;
        ctx.lineWidth = 1;
        ctx.stroke();

        if (progressW > 0) {
          roundedRectPath(ctx, trackX, trackY, progressW, sliderTrackH, trackRadius);
          ctx.fillStyle = row.fillColor;
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(knobX, knobY, sliderTrackH * 0.65, 0, Math.PI * 2);
        ctx.fillStyle = "#e8f7ff";
        ctx.fill();
        ctx.strokeStyle = row.trackStroke;
        ctx.lineWidth = 1;
        ctx.stroke();
      } else {
        (uiRects as Record<string, Rect | undefined>)[row.rectKey] = rect;

        ctx.fillStyle = "#c8d8f0";
        ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(row.label, contentX + 10, curY + rowH / 2);

        const valueText = row.value;
        if (desktop) {
          ctx.font = `bold ${metrics.helperFontPx}px ${GAME_FONT}`;
          const chipW = Math.max(56, Math.ceil(deps.measureTextWidth(valueText) + 14));
          const chipRect: Rect = {
            x: contentRight - chipW - 8,
            y: curY + (rowH - 14) / 2,
            w: chipW,
            h: 14
          };
          deps.drawNotificationChip(chipRect, valueText, row.color, metrics.helperFontPx);
        } else {
          ctx.fillStyle = row.color;
          ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
          ctx.textAlign = "right";
          ctx.fillText(valueText, contentX + contentW - 8, curY + rowH / 2);
        }
      }

      if (alpha < 1) ctx.restore();
      curY += rowH + rowGap;
    }
  }

  // Bottom nav buttons — pinned like friends page ADD/REFER
  const hasPrev = clampedPage > 0;
  const hasNext = clampedPage < totalPages - 1;

  if (hasPrev && hasNext) {
    // Two buttons side by side
    const halfW = Math.floor((contentW - btnGap) / 2);
    const prevRect: Rect = { x: contentX, y: bottomY, w: halfW, h: navBtnH };
    uiRects.settingsBgPrev = prevRect;
    ctx.fillStyle = "#1a2a40";
    ctx.fillRect(prevRect.x, prevRect.y, prevRect.w, prevRect.h);
    ctx.strokeStyle = "#ff6b9d";
    ctx.lineWidth = 1;
    ctx.strokeRect(prevRect.x, prevRect.y, prevRect.w, prevRect.h);
    ctx.fillStyle = "#ff6b9d";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("PREV", prevRect.x + prevRect.w / 2, prevRect.y + prevRect.h / 2);

    const nextRect: Rect = { x: contentX + halfW + btnGap, y: bottomY, w: halfW, h: navBtnH };
    uiRects.settingsBgNext = nextRect;
    ctx.fillStyle = "#1a2a40";
    ctx.fillRect(nextRect.x, nextRect.y, nextRect.w, nextRect.h);
    ctx.strokeStyle = "#ff6b9d";
    ctx.lineWidth = 1;
    ctx.strokeRect(nextRect.x, nextRect.y, nextRect.w, nextRect.h);
    ctx.fillStyle = "#ff6b9d";
    ctx.fillText("NEXT", nextRect.x + nextRect.w / 2, nextRect.y + nextRect.h / 2);
  } else if (hasNext) {
    // Only NEXT — full width
    const nextRect: Rect = { x: contentX, y: bottomY, w: contentW, h: navBtnH };
    uiRects.settingsBgNext = nextRect;
    ctx.fillStyle = "#1a2a40";
    ctx.fillRect(nextRect.x, nextRect.y, nextRect.w, nextRect.h);
    ctx.strokeStyle = "#ff6b9d";
    ctx.lineWidth = 1;
    ctx.strokeRect(nextRect.x, nextRect.y, nextRect.w, nextRect.h);
    ctx.fillStyle = "#ff6b9d";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("NEXT", nextRect.x + nextRect.w / 2, nextRect.y + nextRect.h / 2);
  } else if (hasPrev) {
    // Only PREV — full width
    const prevRect: Rect = { x: contentX, y: bottomY, w: contentW, h: navBtnH };
    uiRects.settingsBgPrev = prevRect;
    ctx.fillStyle = "#1a2a40";
    ctx.fillRect(prevRect.x, prevRect.y, prevRect.w, prevRect.h);
    ctx.strokeStyle = "#ff6b9d";
    ctx.lineWidth = 1;
    ctx.strokeRect(prevRect.x, prevRect.y, prevRect.w, prevRect.h);
    ctx.fillStyle = "#ff6b9d";
    ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("PREV", prevRect.x + prevRect.w / 2, prevRect.y + prevRect.h / 2);
  }
}
